import { useNavigate } from "react-router-dom";
import { Loader2, AlertCircle, CheckCircle, Upload } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import type { Document } from "@/types";

interface DocumentListProps {
  documents: Document[];
}

function StatusBadge({ status }: { status: Document["status"] }) {
  switch (status) {
    case "UPLOADED":
      return (
        <Badge variant="outline" className="gap-1">
          <Upload className="h-3 w-3" />
          Uploaded
        </Badge>
      );
    case "PROCESSING":
      return (
        <Badge variant="secondary" className="gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          Processing
        </Badge>
      );
    case "PROCESSED":
      return (
        <Badge className="gap-1 border-transparent bg-green-600 text-white hover:bg-green-600/80">
          <CheckCircle className="h-3 w-3" />
          Processed
        </Badge>
      );
    case "FAILED":
      return (
        <Badge variant="destructive" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          Failed
        </Badge>
      );
  }
}

export function DocumentList({ documents }: DocumentListProps) {
  const navigate = useNavigate();

  function handleRowClick(doc: Document) {
    if (doc.status === "PROCESSED") {
      navigate(`/documents/${doc.id}`);
    }
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>ID</TableHead>
          <TableHead>File Name</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Upload Date</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {documents.length === 0 ? (
          <TableRow>
            <TableCell colSpan={4} className="text-center text-muted-foreground">
              No documents found.
            </TableCell>
          </TableRow>
        ) : (
          documents.map((doc) => (
            <TableRow
              key={doc.id}
              className={
                doc.status === "PROCESSED"
                  ? "cursor-pointer hover:bg-muted"
                  : ""
              }
              onClick={() => handleRowClick(doc)}
            >
              <TableCell className="font-mono text-xs">{doc.id}</TableCell>
              <TableCell>{doc.originalFileName}</TableCell>
              <TableCell>
                <StatusBadge status={doc.status} />
              </TableCell>
              <TableCell>
                {new Date(doc.uploadDate).toLocaleDateString()}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
